<script lang="ts" setup>
import { formControlInvalidClasses } from '../form-control/classes';
import { formGroupBaseClasses, formGroupLabelClasses, formGroupHelpClasses } from './classes';

defineOptions({
    name: 'IdeoFormGroup'
});

const props = withDefaults(defineProps<{
    label?: string;
    tag?: string;
    labelFor?: string;
    invalidFeedback?: string;
    state?: boolean;
    required?: boolean;
    nospace?: boolean;
    labelClass?: Record<string, boolean> | string[] | string;
}>(), {
    label: null,
    tag: 'span',
    labelFor: null,
    invalidFeedback: null,
    state: null,
    labelClass: () => ({})
});

const slots = useSlots();
const invalid = computed(() => props.state === false || (props.state === null && !!props.invalidFeedback));
const hasActions = computed(() => 'actions' in slots);

const labelClasses = computed(() => ({
    [formGroupLabelClasses]: true,
    'mb-0': hasActions.value,
    'mb-[6px]': !hasActions.value,
    ...normalizeClasses(props.labelClass)
}));

function flag(value: any)
{
    return value !== false;
}

function onClick(e: any)
{
    if (!props.labelFor)
        e.stopPropagation();
}

const groupClasses = computed(() => [
    ...formGroupBaseClasses,
    flag(props.nospace) ? 'mb-0' : 'mb-[16px]',
    invalid.value ? formControlInvalidClasses : null
]);

</script>

<template>
    <div :class="groupClasses">
        <div v-if="label && hasActions" class="mb-[6px] flex min-w-0 items-center gap-[8px]">
            <label :for="labelFor" :class="labelClasses" @click="onClick">
                <component :is="tag" class="[opacity:var(--form-caption-opacity)]" data-form-caption="label">
                    <slot name="label">{{ label }}</slot>
                </component>
                <IdeoTooltip v-if="$slots.help" position="right" multiline>
                    <span class="inline-flex shrink-0 items-center justify-center cursor-help [opacity:var(--form-caption-opacity)]" tabindex="0" :aria-label="$t('[[[Pomoc]]]')">
                        <IdeoIcon name="circle-question-mark" :size="15" aria-hidden="true" />
                    </span>
                    <template #content><slot name="help" /></template>
                </IdeoTooltip>
                <span v-if="flag(required)" class="text-danger dark:text-danger-dark" aria-hidden="true">*</span>
                <span v-if="flag(required)" class="sr-only">{{ $t('[[[Pole wymagane]]]') }}</span>
                <IdeoIcon v-if="invalid" name="triangle-alert" class="text-danger dark:text-danger-dark" :size="12" aria-hidden="true" />
            </label>
            <div class="ms-auto flex shrink-0 items-center" data-form-group-actions><slot name="actions" /></div>
        </div>
        <label v-else-if="label" :for="labelFor" :class="labelClasses" @click="onClick">
            <component :is="tag" class="[opacity:var(--form-caption-opacity)]" data-form-caption="label">
                <slot name="label">{{ label }}</slot>
            </component>
            <IdeoTooltip v-if="$slots.help" position="right" multiline>
                <span class="inline-flex shrink-0 items-center justify-center cursor-help [opacity:var(--form-caption-opacity)]" tabindex="0" :aria-label="$t('[[[Pomoc]]]')">
                    <IdeoIcon name="circle-question-mark" :size="15" aria-hidden="true" />
                </span>
                <template #content><slot name="help" /></template>
            </IdeoTooltip>
            <span v-if="flag(required)" class="text-danger dark:text-danger-dark" aria-hidden="true">*</span>
            <span v-if="flag(required)" class="sr-only">{{ $t('[[[Pole wymagane]]]') }}</span>
            <IdeoIcon v-if="invalid" name="triangle-alert" class="text-danger dark:text-danger-dark" :size="12" aria-hidden="true" />
        </label>
        <slot name="default" />
        <template v-if="$slots.description">
            <small :class="formGroupHelpClasses" data-form-caption="description"><slot name="description" /></small>
        </template>
        <IdeoFormErrorMessage :invalid="invalid" :message="invalidFeedback" />
    </div>
</template>
